import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  User, Users, FileText, CheckCircle, CreditCard, AlertCircle,
  Phone, Mail, MapPin, Globe, Calendar, Shield, Loader2, ChevronRight, X
} from 'lucide-react';

const SC_REASONS = [
  'Perda de passaporte',
  'Roubo de passaporte',
  'Passaporte expirado sem possibilidade de renovacao imediata',
  'Passaporte danificado',
  'Outro motivo',
];

const PASSPORT_STATUS_OPTIONS = [
  { value: 'perdido', label: 'Perdido' },
  { value: 'roubado', label: 'Roubado' },
  { value: 'expirado', label: 'Expirado' },
  { value: 'danificado', label: 'Danificado' },
];

const STEPS = [
  { id: 1, label: 'Identificacao', icon: User },
  { id: 2, label: 'Filiacao e Viagem', icon: Globe },
  { id: 3, label: 'Confirmacao', icon: CheckCircle },
];

export default function SalvoConduto() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [rgpdAccepted, setRgpdAccepted] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    birth_date: '', birth_place: '', nationality: 'Angolana',
    last_passport_number: '', passport_status: '',
    address_spain: '', city: '', postal_code: '',
    father_name: '', mother_name: '',
    destination_country: '', departure_date: '', reason: '',
    police_report_number: '', notes: '',
  });

  useEffect(() => {
    if (user) {
      const parts = (user.full_name || '').split(' ');
      setForm(prev => ({
        ...prev,
        first_name: parts.slice(0, -1).join(' ') || prev.first_name,
        last_name: parts.slice(-1).join(' ') || prev.last_name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        birth_date: user.birth_date || prev.birth_date,
        address_spain: user.address || prev.address_spain,
        city: user.city || prev.city,
      }));
    }
  }, [user]);

  useEffect(() => {
    const ref = searchParams.get('ref');
    const paymentSuccess = searchParams.get('payment_success');
    if (ref && paymentSuccess === 'true') {
      setResult({ reference_code: ref, payment_confirmed: true });
      setStep(4);
    }
  }, [searchParams]);

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const canNext = () => {
    if (step === 1) return form.first_name && form.last_name && form.email && form.phone && form.birth_date && form.birth_place && form.passport_status && form.address_spain && form.city;
    if (step === 2) return form.destination_country && form.reason;
    return true;
  };

  const handleSubmit = async () => {
    if (!rgpdAccepted) { toast.error('Deve aceitar os termos RGPD'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        full_name: `${form.first_name} ${form.last_name}`,
        rgpd_accepted: true,
        rgpd_accepted_at: new Date().toISOString(),
      };
      const res = await api.post('/salvo-conduto', payload);
      setResult(res.data);
      toast.success('Pedido registado com sucesso!');
      try {
        const payRes = await api.post(`/salvo-conduto/${res.data.id}/payment`);
        if (payRes.data?.url) setPaymentUrl(payRes.data.url);
      } catch { /* payment optional */ }
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao submeter pedido');
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#CC0000]/30 focus:border-[#CC0000] outline-none transition-all";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight size={12} />
            <span className="text-white">Salvo-Conduto</span>
          </div>
          <div className="flex items-center gap-3">
            <Shield size={28} className="text-[#FFD700]" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" data-testid="scond-title">Salvo-Conduto</h1>
              <p className="text-gray-400 text-sm mt-1">Documento de viagem de emergencia para cidadaos angolanos</p>
            </div>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
            <CreditCard size={14} />
            <span>Taxa: <strong>EUR 10,00</strong></span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      {step <= 3 && (
        <div className="bg-white border-b sticky top-0 z-20">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex gap-1">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const isActive = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive ? 'bg-[#CC0000] text-white' : isDone ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'
                  }`}>
                    {isDone ? <CheckCircle size={14} /> : <Icon size={14} />}
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Step 1: Identificacao */}
        {step === 1 && !result && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="scond-step1">
            <div className="flex items-center gap-3">
              <User size={24} className="text-[#CC0000]" />
              <h2 className="text-lg font-bold text-gray-900">Identificacao</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Primeiro(s) Nome(s) *</label>
                <input className={inputCls} value={form.first_name} onChange={set('first_name')} placeholder="Primeiro(s) nome(s)" data-testid="scond-firstname" />
              </div>
              <div>
                <label className={labelCls}>Apelido(s) *</label>
                <input className={inputCls} value={form.last_name} onChange={set('last_name')} placeholder="Apelido(s)" data-testid="scond-lastname" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" className={inputCls} value={form.email} onChange={set('email')} data-testid="scond-email" />
              </div>
              <div>
                <label className={labelCls}>Telefone *</label>
                <input type="tel" className={inputCls} value={form.phone} onChange={set('phone')} data-testid="scond-phone" />
              </div>
              <div>
                <label className={labelCls}>Data de Nascimento *</label>
                <input type="date" className={inputCls} value={form.birth_date} onChange={set('birth_date')} data-testid="scond-birthdate" />
              </div>
              <div>
                <label className={labelCls}>Naturalidade *</label>
                <input className={inputCls} value={form.birth_place} onChange={set('birth_place')} placeholder="Local de nascimento" data-testid="scond-birthplace" />
              </div>
              <div>
                <label className={labelCls}>Nacionalidade</label>
                <input className={inputCls} value={form.nationality} onChange={set('nationality')} />
              </div>
              <div>
                <label className={labelCls}>N. Passaporte (se conhecido)</label>
                <input className={inputCls} value={form.last_passport_number} onChange={set('last_passport_number')} placeholder="Ultimo n. conhecido" data-testid="scond-passport" />
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>Situacao do Passaporte *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PASSPORT_STATUS_OPTIONS.map(opt => (
                    <button key={opt.value} type="button" onClick={() => setForm(p => ({ ...p, passport_status: opt.value }))} data-testid={`scond-passport-${opt.value}`}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${form.passport_status === opt.value ? 'bg-[#CC0000] text-white border-[#CC0000]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {(form.passport_status === 'perdido' || form.passport_status === 'roubado') && (
                <div className="md:col-span-2">
                  <label className={labelCls}>N. do Relatorio Policial</label>
                  <input className={inputCls} value={form.police_report_number} onChange={set('police_report_number')} placeholder="Numero da queixa" data-testid="scond-police-report" />
                </div>
              )}

              <div className="md:col-span-2">
                <label className={labelCls}>Morada em Espanha *</label>
                <input className={inputCls} value={form.address_spain} onChange={set('address_spain')} data-testid="scond-address" />
              </div>
              <div>
                <label className={labelCls}>Cidade *</label>
                <input className={inputCls} value={form.city} onChange={set('city')} data-testid="scond-city" />
              </div>
              <div>
                <label className={labelCls}>Codigo Postal</label>
                <input className={inputCls} value={form.postal_code} onChange={set('postal_code')} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Filiacao e Viagem */}
        {step === 2 && !result && (
          <div className="bg-white rounded-xl border p-6 space-y-6" data-testid="scond-step2">
            {/* Filiacao */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Users size={24} className="text-[#CC0000]" />
                <h2 className="text-lg font-bold text-gray-900">Filiacao</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Nome do Pai</label>
                  <input className={inputCls} value={form.father_name} onChange={set('father_name')} placeholder="Nome completo" />
                </div>
                <div>
                  <label className={labelCls}>Nome da Mae</label>
                  <input className={inputCls} value={form.mother_name} onChange={set('mother_name')} placeholder="Nome completo" />
                </div>
              </div>
            </div>

            <hr />

            {/* Viagem */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Globe size={24} className="text-[#CC0000]" />
                <h2 className="text-lg font-bold text-gray-900">Dados da Viagem</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Pais de Destino *</label>
                  <input className={inputCls} value={form.destination_country} onChange={set('destination_country')} placeholder="Ex: Angola" data-testid="scond-destination" />
                </div>
                <div>
                  <label className={labelCls}>Data Prevista de Partida</label>
                  <input type="date" className={inputCls} value={form.departure_date} onChange={set('departure_date')} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Motivo *</label>
                  <div className="space-y-2">
                    {SC_REASONS.map(r => (
                      <label key={r} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${form.reason === r ? 'border-[#CC0000] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input type="radio" name="reason" value={r} checked={form.reason === r} onChange={() => setForm(p => ({ ...p, reason: r }))} className="accent-[#CC0000]" />
                        <span className="text-sm text-gray-700">{r}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Observacoes</label>
                  <textarea className={inputCls} rows={2} value={form.notes} onChange={set('notes')} placeholder="Informacoes adicionais..." />
                </div>
              </div>
            </div>

            {/* RGPD */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Proteccao de Dados (RGPD)</h3>
              <p className="text-xs text-blue-700 mb-3">
                Os seus dados pessoais serao tratados exclusivamente para a emissao do Salvo-Conduto, nos termos do RGPD.
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={rgpdAccepted} onChange={e => setRgpdAccepted(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500" data-testid="scond-rgpd" />
                <span className="text-xs text-gray-600">Li e aceito os termos de proteccao de dados pessoais.</span>
              </label>
            </div>
          </div>
        )}

        {/* Step 3: Confirmacao */}
        {step === 3 && !result && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="scond-step3">
            <div className="flex items-center gap-3">
              <CheckCircle size={24} className="text-green-600" />
              <h2 className="text-lg font-bold text-gray-900">Confirmacao dos Dados</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl border">
                <h3 className="font-semibold text-gray-800 text-sm mb-3 pb-2 border-b">Identificacao</h3>
                <div className="space-y-2 text-xs">
                  {[['Nome', `${form.first_name} ${form.last_name}`], ['Nascimento', form.birth_date], ['Naturalidade', form.birth_place], ['Passaporte', form.last_passport_number || '-'], ['Situacao', form.passport_status], ['Telefone', form.phone], ['Email', form.email]].map(([l, v]) => (
                    <div key={l} className="flex justify-between"><span className="text-gray-500">{l}</span><span className="text-gray-800 font-medium">{v}</span></div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border">
                <h3 className="font-semibold text-gray-800 text-sm mb-3 pb-2 border-b">Viagem</h3>
                <div className="space-y-2 text-xs">
                  {[['Destino', form.destination_country], ['Partida', form.departure_date || '-'], ['Motivo', form.reason], ['Pai', form.father_name || '-'], ['Mae', form.mother_name || '-']].map(([l, v]) => (
                    <div key={l} className="flex justify-between"><span className="text-gray-500">{l}</span><span className="text-gray-800 font-medium text-right max-w-[60%]">{v}</span></div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-5 bg-gradient-to-r from-[#CC0000]/10 to-[#FFD700]/10 rounded-xl border border-[#CC0000]/20 text-center">
              <p className="text-lg font-bold text-[#CC0000]">Taxa: EUR 10,00</p>
              <p className="text-sm text-gray-600 mt-1">Validade: 30 dias apos emissao</p>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {result && (
          <div className="bg-white rounded-xl border p-10 text-center" data-testid="scond-result">
            {result.payment_confirmed ? (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-green-600" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Pagamento Confirmado!</h2>
                <p className="text-gray-500 mb-4">O seu pedido de Salvo-Conduto esta a ser processado.</p>
                <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 px-4 py-2 rounded-lg text-sm text-green-800">
                  <FileText size={14} /> Referencia: <strong>{result.reference_code}</strong>
                </div>
                <p className="mt-4 text-sm text-gray-400">Recebera uma notificacao por email quando o documento estiver pronto.</p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="text-blue-600" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Pedido Registado!</h2>
                <p className="text-gray-500 mb-1">Referencia: <strong className="text-[#CC0000]">{result.reference_code}</strong></p>
                <p className="text-sm text-gray-400 mb-6">Taxa: EUR {result.fee?.toFixed(2) || '10.00'}</p>
                {paymentUrl ? (
                  <a href={paymentUrl} data-testid="scond-pay-btn"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#CC0000] text-white text-lg font-medium rounded-xl hover:bg-[#A30000] transition-colors">
                    <CreditCard size={20} /> Pagar Agora (EUR 10,00)
                  </a>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 inline-block">
                    <div className="flex items-center gap-2 text-amber-800 text-sm">
                      <AlertCircle size={16} />
                      <span>O link de pagamento sera enviado por email.</span>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-center gap-4 mt-8">
              <Link to="/" className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Voltar ao Inicio</Link>
              <Link to="/servicos-consulares" className="px-6 py-3 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000]">Ver Outros Servicos</Link>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {step <= 3 && !result && (
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
              Anterior
            </button>
            {step < 3 ? (
              <button onClick={() => { if (canNext()) setStep(step + 1); else toast.error('Preencha todos os campos obrigatorios'); }} disabled={!canNext()}
                className="px-8 py-3 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000] disabled:opacity-40 flex items-center gap-2" data-testid="scond-next">
                Seguinte <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting || !rgpdAccepted}
                className="px-8 py-3 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 flex items-center gap-2" data-testid="scond-submit">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> A processar...</> : <><CheckCircle size={16} /> Submeter Pedido</>}
              </button>
            )}
          </div>
        )}

        {/* Info box */}
        {step <= 3 && !result && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="scond-info">
            <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2"><AlertCircle size={14} /> Informacoes sobre o Salvo-Conduto</h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li>O Salvo-Conduto e um documento de viagem de emergencia, valido para uma unica viagem.</li>
              <li>E emitido quando o passaporte foi perdido, roubado, expirado ou esta danificado.</li>
              <li>Para casos de perda ou roubo, e recomendavel apresentar o relatorio policial.</li>
              <li>Prazo de emissao: ate 5 dias uteis apos aprovacao e pagamento.</li>
              <li>Apresente-se na Embaixada com um documento de identificacao valido.</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
